// eslint-disable-next-line import/no-cycle
import browserDetect from 'browser-detect';
import isbot from 'isbot';
import Cookies from 'js-cookie';
import get from 'lodash/get';
import getContext from '../utilities/get-context';
// eslint-disable-next-line import/no-cycle
import clientOnly from '../utilities/client-only';
import getVisitorId from './visitorId';
import { getSessionId } from './sessionManager';
import { COOKIES } from '@/constants/cookies';

function getUserId() {
  return clientOnly(() => getContext().__MA__.user.id || undefined);
}

function hasIncentive() {
  return getContext().__MA__.incentive;
}

function getUserStatus(planName) {
  const userId = getUserId();
  if (typeof userId === 'undefined' && !planName) {
    return 'guest';
  }

  const usersPlanName = planName ? planName : getContext().__MA__.user.plan_name;

  return usersPlanName === 'Free' ? 'Free trial' : 'subscriber';
}

function getIsNewVisitor() {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return !getContext().localStorage.getItem('notFirstTimeSession');
}

function getUserPlan(planName) {
  // user.plan_name will return 'Team' for user that have active seats on a team plan, and 'Free' for free team admins.
  const usersPlanName = planName ? planName : getContext().__MA__.user.plan_name;
  return clientOnly(() => usersPlanName || undefined);
}

function getLeadTypeIndication(user) {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return user.leadType || Cookies.get(COOKIES.NAMES.LEAD_INFO_CLASSIFICATION) || null;
}

function getLeadJourney(user) {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const leadInfoJourney = Cookies.get(COOKIES.NAMES.LEAD_INFO_JOURNEY);
  return user.leadInfoJourney || (leadInfoJourney && JSON.parse(leadInfoJourney)) || [];
}

export function addToSuperProperties(properties) {
  if (typeof window === 'undefined') {
    return;
  }

  if (!(window.__super_properties__ instanceof Object)) {
    window.__super_properties__ = {};
  }
  window.__super_properties__ = { ...window.__super_properties__, ...properties };
}

export const removeFromSuperProperties = property => {
  if (typeof window === 'undefined') {
    return;
  }
  if (Object.prototype.hasOwnProperty.call(window.__super_properties__, property)) {
    delete window.__super_properties__[property];
  }
};

export function setRouteSuperProperties(route) {
  if (typeof window === 'undefined') {
    return;
  }

  const properties = {
    domain: getContext().location.hostname,
    page: route.path,
    $current_url: route.path,
    page_url: getContext().location.origin + route.fullPath,
    // todo assets_filter seems to be broken. We need to re-look at this. It should probably be set whenever a search
    // is performed, and shouldn't only read from query string. Usually categories are set in the URL path
    assets_filter: new URLSearchParams(getContext().location.search).get('categories'),
    utm_campaign: route.query.utm_campaign ?? null,
    utm_content: route.query.utm_content ?? null,
    utm_medium: route.query.utm_medium ?? null,
    utm_source: route.query.utm_source ?? null,
    utm_term: route.query.utm_term ?? null,
  };

  addToSuperProperties(properties);
}

export function setMixpanelSuperProperties(mixpanel) {
  const properties = {
    $device_id: mixpanel.get_property('$device_id'),
    $initial_referrer: mixpanel.get_property('$initial_referrer'),
    $initial_referring_domain: mixpanel.get_property('$initial_referring_domain'),
    $insert_id: mixpanel.get_property('$insert_id'),
    $region: mixpanel.get_property('$region'),
    $search_engine: mixpanel.get_property('$search_engine'),
    $city: mixpanel.get_property('$city'),
    $lib_version: mixpanel.get_property('$lib_version'),
  };

  addToSuperProperties(properties);
}

export function setBaseSuperProperties() {
  const userData = getContext().__MA__.user;

  const browserMeta = browserDetect();
  const country = get(userData, 'visiting_country', '');
  const properties = {
    $user_id: getUserId(),
    session_id: getSessionId(),
    visitor_id: getVisitorId() || Cookies.get(COOKIES.NAMES.ANONYMOUS_ID),
    timestamp: new Date().toISOString(),
    referrer: document.referrer,
    $browser: browserMeta.name,
    language: getContext().navigator.language,
    $os: browserMeta.os,
    user_agent: navigator.userAgent,
    $state: country === 'US' ? get(userData, 'visiting_state', '') : null,
    country,
    email: get(userData, 'email', ''),
    new_visitor: getIsNewVisitor(),
    gclid: get(getContext().ga, 'ya', ''),
    is_bot: isbot(navigator.userAgent),
    user_status: getUserStatus(),
    device_type: browserMeta.mobile ? 'Mobile' : 'Desktop',
    is_active_incentive_cookie: hasIncentive(),
    active_subscription: getUserPlan(),
    $screen_height: getContext().screen.height,
    $screen_width: getContext().screen.width,
    is_new_billing: getContext().__MA__.user?.uses_billing_engine,
    $billing_flow: getContext().__MA__.AB_TEST_PAYMENT_FORM === 'chargebee' ? 'CB' : 'MA',
    vat_presented_in: country === 'IL' ? 'pricing page' : null,
    lead_type_indication: getLeadTypeIndication(userData),
    journey: getLeadJourney(userData),
  };

  addToSuperProperties(properties);
}

export function resetUserProperties({ userId, email, planName, usesBillingEngine }) {
  const properties = {
    ...(userId && { $user_id: userId, user_id: userId }),
    ...(email && { email }),
    ...(planName && {
      user_status: getUserStatus(planName),
      active_subscription: getUserPlan(planName),
    }),
    ...(usesBillingEngine && { is_new_billing: getUserPlan(usesBillingEngine) }),
  };
  addToSuperProperties(properties);
}

function getScreenWidth() {
  return {
    screen_absolute_width: getContext().screen.width,
    screen_viewport_width: getContext().innerWidth,
  };
}

export function getSuperProperties() {
  return { ...window.__super_properties__, ...getScreenWidth() };
}
